import { useEffect, useRef } from "react";

/**
 * Applies the 'is-visible' class to elements matching `.reveal` inside `ref`
 * when they enter the viewport. Uses IntersectionObserver + MutationObserver
 * so newly-mounted `.reveal` elements (e.g., from filter changes) are also
 * picked up.
 */
export const useReveal = () => {
    const ref = useRef(null);

    useEffect(() => {
        const scope = ref.current || document;

        if (!("IntersectionObserver" in window)) {
            scope.querySelectorAll(".reveal").forEach((el) =>
                el.classList.add("is-visible")
            );
            return;
        }

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("is-visible");
                        io.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
        );

        const observed = new WeakSet();
        const observeAll = () => {
            scope.querySelectorAll(".reveal").forEach((el) => {
                if (observed.has(el)) return;
                observed.add(el);
                if (el.classList.contains("is-visible")) return;
                io.observe(el);
            });
        };
        observeAll();

        // Watch for later additions (e.g., after route/filter change)
        const mo = new MutationObserver(observeAll);
        mo.observe(scope, { childList: true, subtree: true });

        return () => {
            io.disconnect();
            mo.disconnect();
        };
    }, []);

    return ref;
};
